import type { PromoAction, PromotionData } from "@/lib/promotion-types";

/**
 * Динамическая колонка «Акции»: в сохранённом виде хранится ОДИН маркер
 * `promo_actions`, а при отрисовке/экспорте он разворачивается в колонку на
 * каждую ТЕКУЩУЮ акцию (`promo_action:<id>`). Так набор всегда актуален: акция
 * ушла — колонка исчезла, пришла — появилась. (В отличие от «Дневника», который
 * разворачивается при сохранении — там ключи дней самосвежие, а id акций нет.)
 */
export const PROMO_ACTIONS_MARKER_KEY = "promo_actions";
const PROMO_ACTION_PREFIX = "promo_action:";

export function isPromoActionsMarkerKey(key: string): boolean {
  return key === PROMO_ACTIONS_MARKER_KEY;
}

// Строгая проверка с двоеточием — чтобы НЕ зацепить легаси-ключ promo_action_price:<id>.
export function isPromoActionColumnKey(key: string): boolean {
  return key.startsWith(PROMO_ACTION_PREFIX);
}

export function promoActionColumnKey(actionId: string | number): string {
  return `${PROMO_ACTION_PREFIX}${actionId}`;
}

export function parsePromoActionId(key: string): string | null {
  return isPromoActionColumnKey(key) ? key.slice(PROMO_ACTION_PREFIX.length) : null;
}

/**
 * Самая высокая цена из вариантов акции для одного товара.
 *
 * Для продавца это выгодный вариант: чем выше цена по акции, тем меньше
 * скидка за его счёт. Сравниваем и уже подключённые, и доступные акции —
 * выбор не должен зависеть от того, успел ли товар войти в акцию.
 */
export function highestPromoOptionPrice(data: PromotionData | null | undefined, offerKeys: Iterable<string>): number | null {
  if (!data) return null;
  const keys = [...new Set(offerKeys)];
  let highest = 0;
  for (const offerKey of keys) {
    for (const stats of Object.values(data.byOffer?.[offerKey] ?? {})) {
      if (stats.action_price > highest) highest = stats.action_price;
    }
    for (const byOffer of Object.values(data.candidatePrices ?? {})) {
      const price = byOffer[offerKey] ?? 0;
      if (price > highest) highest = price;
    }
  }
  return highest || null;
}

const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

function promoDateSuffix(dateStart: string, dateEnd: string): string {
  const now = new Date();
  const start = new Date(dateStart);
  const end = new Date(dateEnd);
  if (Number.isNaN(end.getTime())) return "";
  if (!Number.isNaN(start.getTime()) && start > now) return ` (от ${start.getDate()} ${MONTHS_SHORT[start.getMonth()]})`;
  return ` (до ${end.getDate()} ${MONTHS_SHORT[end.getMonth()]})`;
}

/** Технический хвост Ozon у бессрочной «Эластичной бустинг»-акции не является
 * названием и только засоряет узкие шапки. Убираем его в общем отображаемом
 * названии, оставляя исходное значение в кэше без изменений. */
export function promoActionDisplayTitle(action: Pick<PromoAction, "title">): string {
  const title = action.title.replace(/\s+/g, " ").trim();
  const withoutUnlimitedDuration = title
    .replace(/\s*(?:[·,;:—–-]\s*)?без ограничения срока действия(?!\p{L})/giu, " ")
    .replace(/\s+/g, " ")
    .replace(/[·,;:—–-]\s*$/u, "")
    .trim();
  return withoutUnlimitedDuration || "Акция";
}

export function promoActionLabel(action: PromoAction): string {
  return promoActionDisplayTitle(action) + promoDateSuffix(action.date_start, action.date_end);
}

export type PromoActionHeaderParts = { tag: string | null; label: string };

/**
 * Шапка не сокращает название акции: важные слова остаются доступны для
 * сканирования. Повторяющийся тип («Распродажа», «Распродажа стока», «Акция»,
 * «Скидка») выносим в маленькую метку, а оставшиеся слова становятся названием.
 * Так соседние колонки легче отличать друг от друга, не теряя ни одного слова.
 */
export function promoActionHeaderParts(action: PromoAction): PromoActionHeaderParts {
  const title = promoActionDisplayTitle(action);
  // Сначала длинное устойчивое словосочетание: «Распродажа стока» — один тип,
  // поэтому в шапке оно должно быть одной меткой, а не «Распродажа» + «стока».
  const match = title.match(/распродаж[а-яё]*\s+сток[а-яё]*|распродаж[а-яё]*|акци[яи]|скидк[а-яё]*/iu);
  if (!match || match.index == null) return { tag: null, label: title };
  const label = `${title.slice(0, match.index)} ${title.slice(match.index + match[0].length)}`.replace(/\s+/g, " ").trim();
  return { tag: match[0], label };
}

/** Подпись колонки по ключу `promo_action:<id>` и списку акций (для заголовков/экспорта). */
export function promoActionLabelForKey(key: string, actions: PromoAction[]): string {
  const id = parsePromoActionId(key);
  const action = id != null ? actions.find(a => String(a.id) === id) : null;
  return action ? promoActionLabel(action) : "Акция";
}

/** Части шапки динамической промо-колонки без потери слов названия. */
export function promoActionHeaderPartsForKey(key: string, actions: PromoAction[]): PromoActionHeaderParts {
  const id = parsePromoActionId(key);
  const action = id != null ? actions.find(a => String(a.id) === id) : null;
  return action ? promoActionHeaderParts(action) : { tag: null, label: "Акция" };
}

/** Стабильный порядок акций по умолчанию: по дате старта, затем по названию. */
export function sortPromoActions(actions: PromoAction[]): PromoAction[] {
  return [...actions].sort(
    (a, b) => (a.date_start || "").localeCompare(b.date_start || "") || a.title.localeCompare(b.title, "ru"),
  );
}

/**
 * Порядок акций с учётом пользовательского `order` (массив id): сначала акции в
 * сохранённом порядке, затем новые (которых в order нет) — в конец, по умолчанию.
 * Ушедшие акции (id есть в order, но акции уже нет) просто выпадают.
 */
export function orderPromoActions(actions: PromoAction[], order: string[] = []): PromoAction[] {
  if (!order.length) return sortPromoActions(actions);
  const byId = new Map(actions.map(a => [String(a.id), a]));
  const used = new Set<string>();
  const out: PromoAction[] = [];
  for (const id of order) {
    const sid = String(id);
    const a = byId.get(sid);
    if (a && !used.has(sid)) { used.add(sid); out.push(a); }
  }
  return [...out, ...sortPromoActions(actions.filter(a => !used.has(String(a.id))))];
}

/**
 * Разворачивает маркер `promo_actions` в `promo_action:<id>` по каждой текущей
 * акции (с учётом пользовательского порядка `order`). Остальные ключи без
 * изменений. Применять ПРИ ОТРИСОВКЕ/ЭКСПОРТЕ, не при сохранении.
 */
export function expandPromoActionsColumns(columns: string[], actions: PromoAction[], order: string[] = []): string[] {
  if (!columns.includes(PROMO_ACTIONS_MARKER_KEY)) return columns;
  const promoKeys = orderPromoActions(actions, order).map(a => promoActionColumnKey(a.id));
  const out: string[] = [];
  const seen = new Set<string>();
  for (const key of columns) {
    if (key === PROMO_ACTIONS_MARKER_KEY) {
      for (const pk of promoKeys) if (!seen.has(pk)) { seen.add(pk); out.push(pk); }
    } else if (!seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  return out;
}

/**
 * Обратная операция для редактора колонок таблицы новой версии: колонки
 * отдельных акций (`promo_action:<id>`) → один маркер `promo_actions` на месте
 * первой из них. Редактор получает уже развёрнутый список (так таблица знает
 * ширины и подписи колонок), а в ленте выбранных колонок акции должны быть
 * одним пунктом «Акции». Сохраняется именно маркер — тогда набор колонок
 * остаётся живым: акция кончилась — колонка исчезла сама.
 */
export function collapsePromoActionColumns(columns: string[]): string[] {
  const out: string[] = [];
  let collapsed = false;
  for (const key of columns) {
    if (isPromoActionColumnKey(key) || isPromoActionsMarkerKey(key)) {
      if (!collapsed) {
        out.push(PROMO_ACTIONS_MARKER_KEY);
        collapsed = true;
      }
      continue;
    }
    if (!out.includes(key)) out.push(key);
  }
  return out;
}
